// stores/startupStore.js
import { create } from "zustand";
import EditorJsDiff from "../components/Startups/EditorJsDiff";
import fetchApi from "../components/Lib/api";
import config from "../components/Lib/environment";
import Mixpanel from "../components/Lib/mixpanel";

const useStartupStore = create((set, get) => ({
  id: null,
  startup: null, // Holds the startup_entity
  program: null, // Holds the program related to the startup
  isOwner: false, // Whether the user is the owner of the startup
  isMember: false, // Whether the user is a member of the startup
  role: null, // The role of the user in the startup
  isFollowing: false, // Whether the user is following the startup
  existingJoinRequest: false, // Whether there is an existing join request
  editMode: false,
  members: [],
  openings: [],
  slug: "",
  entity: "Startup",
  userInitials: "AM",
  content: {}, // { [pageId]: { [index]: content } }
  privacy: {}, // { [pageId]: { [index]: privacy } }
  files: {}, // { [pageId]: { [index]: files } }
  pendingChanges: {},
  selectedProgram: {
    switch: false,
    cid: 0, // pageId
    sid: 0, // index
  },
  roadmapId: "",
  openChat: false,
  openForApplication: false,
  showFunding: false,
  startActivity: false,
  startTime: Date.now(),
  pendingJoinRequest: 0,
  isSubmitted: false, // checks whether the project has been submmited or not
  submittedAt: "",
  // UI flags
  suggestedTasksLoading: false,


  setEditorState: () => {
    console.log("Initializing editor state");
    set((state) => {
      const categories = state.program?.categories || [];
  
      const updatedContent = {};
      const updatedPrivacy = {};
      const updatedFiles = {};
  
      categories.forEach((category, categoryIndex) => {
        const category_id = category.id;
        const subCategories = category.sub_categories || [];
  
        const pageContent = {};
        const pagePrivacy = {};
        const pageFiles = {};
  
        subCategories.forEach((subCategory, subIndex) => {
          pageContent[subIndex] = {
            response: subCategory.proposed_response || subCategory.response,
            originalResponse: subCategory.response, // Keep last committed for diff
            diff: subCategory.diff || null, // Include diff from backend
            touched: false,
            id: subCategory.id,
            category_id,
            name: subCategory.name,
            guide: subCategory.guide,
            outcome: subCategory.outcome,
            wait: subCategory.waiting_for_approval,
            chats: subCategory.limited_conversation_histories,
            sync_status: subCategory.sync_status,
            notion_block_id: subCategory.notion_block_id,
            sync_conflict: subCategory.sync_conflict || null, 
            tags: subCategory.tags || [],
          };
          pagePrivacy[subIndex] = subCategory.privacy;
          pageFiles[subIndex] = [
            ...subCategory.file_records,
            ...subCategory.files,
          ];
        });
  
        updatedContent[categoryIndex] = pageContent;
        updatedPrivacy[categoryIndex] = pagePrivacy;
        updatedFiles[categoryIndex] = pageFiles;
      });
  
      // Retrieve the query parameters from the URL:
      const params = new URLSearchParams(window.location.search);
      let cid = parseInt(params.get("cid"), 10);
      let sid = parseInt(params.get("sid"), 10);
  
      // Validate the category index
      if (isNaN(cid) || cid < 0 || cid >= categories.length) {
        cid = state.selectedProgram.cid || 0;
      }
      // Validate the sub-category index if there is a valid category.
      const subCategories = categories[cid]?.sub_categories || [];
      if (isNaN(sid) || sid < 0 || sid >= subCategories.length) {
        sid = state.selectedProgram.sid || 0;
      }
  
      return {
        content: updatedContent,
        privacy: updatedPrivacy,
        files: updatedFiles,
        selectedProgram: {
          switch: false,
          cid,
          sid,
        },
      };
    });
  },
  

  setContent: (response, guide) => {
    set((state) => {
      const categoryIndex = state.selectedProgram.cid;
      const subIndex = state.selectedProgram.sid;

      // Update content
      const updatedCategoryContent = {
        ...(state.content[categoryIndex] || {}),
        [subIndex]: {
          ...(state.content[categoryIndex]?.[subIndex] || {}),
          response:
            response || state.content[categoryIndex]?.[subIndex]?.response,
          guide: guide || state.content[categoryIndex]?.[subIndex]?.guide,
          touched: true,
          name: state.content[categoryIndex]?.[subIndex]?.name,
        },
      };

      // Track change
      const pendingChanges = {
        ...state.pendingChanges,
        [categoryIndex]: {
          ...(state.pendingChanges[categoryIndex] || {}),
          [subIndex]: {
            ...(state.pendingChanges[categoryIndex]?.[subIndex] || {}),
            content: true,
          },
        },
      };

      return {
        content: {
          ...state.content,
          [categoryIndex]: updatedCategoryContent,
        },
        pendingChanges,
      };
    });
  },

  updateSyncStatus: (pageId, index, newStatus) => {
    const content = get().content;
    const pageContent = { ...(content[pageId] || {}) };
  
    if (pageContent[index]) {
      pageContent[index] = {
        ...pageContent[index],
        sync_status: newStatus,
      };
  
      set((state) => ({
        content: {
          ...state.content,
          [pageId]: pageContent,
        },
      }));
    }
  },
  

  setPrivacy: (c) => {
    set((state) => {
      const categoryIndex = state.selectedProgram.cid;
      const subIndex = state.selectedProgram.sid;

      // Update privacy
      const updatedCategoryPrivacy = {
        ...(state.privacy[categoryIndex] || {}),
        [subIndex]: c,
      };

      // Track change
      const pendingChanges = {
        ...state.pendingChanges,
        [categoryIndex]: {
          ...(state.pendingChanges[categoryIndex] || {}),
          [subIndex]: {
            ...(state.pendingChanges[categoryIndex]?.[subIndex] || {}),
            privacy: true,
          },
        },
      };

      return {
        privacy: {
          ...state.privacy,
          [categoryIndex]: updatedCategoryPrivacy,
        },
        pendingChanges,
      };
    });
  },

  setFiles: (newFiles) => {
    set((state) => {
      const categoryIndex = state.selectedProgram.cid;
      const subIndex = state.selectedProgram.sid;

      // Update files
      const updatedCategoryFiles = {
        ...(state.files[categoryIndex] || {}),
        [subIndex]: newFiles,
      };

      // Track change
      const pendingChanges = {
        ...state.pendingChanges,
        [categoryIndex]: {
          ...(state.pendingChanges[categoryIndex] || {}),
          [subIndex]: {
            ...(state.pendingChanges[categoryIndex]?.[subIndex] || {}),
            files: true,
          },
        },
      };

      return {
        files: {
          ...state.files,
          [categoryIndex]: updatedCategoryFiles,
        },
        pendingChanges,
      };
    });
  },

  setEditMode: (isOpened) => set({ editMode: isOpened }),
  setIsFollowing: (follow) => set({ isFollowing: follow }),

  // Setters for startup-related fields
  setStartup: async (data) => {
    console.log(data)
    set({
      startup: data.startup_entity,
      id: data.startup_entity.id,
      program: data.startup_entity.program
        ? {
            ...data.startup_entity.program,
            categories: data.startup_entity.program?.categories
              .map((category) => {
                const filteredSubCategories = category.sub_categories.filter(
                  (subCategory) =>
                    subCategory.is_active &&
                    (subCategory.privacy === "Everyone" ||
                      (subCategory.privacy === "Only me" && data.is_owner) ||
                      (subCategory.privacy === "Members" && data.is_member))
                );
  
                return filteredSubCategories.length > 0
                  ? {
                      ...category,
                      sub_categories: filteredSubCategories,
                    }
                  : null;
              })
              .filter((category) => category !== null),
          }
        : { loading: true, categories: [] },
      isOwner: data.is_owner,
      slug: data.startup_entity.slug,
      isMember: data.is_member,
      role: data.role,
      isFollowing: data.is_following,
      existingJoinRequest: data.existing_join_request,
      openings: data.startup_entity.openings,
      members: data.startup_entity.members,
      entity: data.startup_entity.entity_type,
      pendingJoinRequest: data.pending_join_requests_count,
      roadmapId: data.startup_entity?.program?.roadmap_id,
      openForApplication: data.startup_entity.open_fo_applications,
      isSubmitted: data.startup_entity.is_submitted,
      submittedAt: data.startup_entity.submitted_at
    });
  
    if (data.startup_entity.program) {
      get().setEditorState();
    } else {
      set({ suggestedTasksLoading: true });
      // Start timing for roadmap generation process
      const processStartTime = performance.now();
      
      // Track process start
      Mixpanel.custom('Roadmap Generation Started', {
        startup_slug: data.startup_entity.slug,
        entity_type: data.startup_entity.entity_type,
        is_owner: data.is_owner,
        is_member: data.is_member
      });
  
      try {
        // 1. Fetch Summary
        const summaryStartTime = performance.now();
        const summaryResponse = await fetchApi(
          `/startup_entities/${data.startup_entity.slug}/summary`
        );
        
        if (!summaryResponse.ok) {
          const summaryTime = performance.now() - summaryStartTime;
          Mixpanel.error('summary_fetch', {
            message: 'Failed to fetch summary payload',
            startup_slug: data.startup_entity.slug,
            status_code: summaryResponse.status,
            response_time: summaryTime
          });
          console.error("Failed to fetch summary payload", summaryResponse);
          return;
        }
        
        const summaryPayload = await summaryResponse.json();
        const summaryTime = performance.now() - summaryStartTime;
        
        Mixpanel.performance('Summary Fetch', {
          responseTime: summaryTime,
          loadTime: summaryTime
        }, {
          startup_slug: data.startup_entity.slug,
          success: true
        });
        
        console.log("Summary payload:", summaryPayload);
  
        // 2. Generate Skeleton
        const skeletonStartTime = performance.now();
        const skeletonResponse = await fetch(config.roadmapUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "User-Agent": "RoadmapClient/1.0",
          },
          body: JSON.stringify(summaryPayload),
        });
  
        if (!skeletonResponse.ok) {
          const skeletonTime = performance.now() - skeletonStartTime;
          Mixpanel.error('skeleton_generation', {
            message: 'Skeleton generation failed',
            startup_slug: data.startup_entity.slug,
            status_code: skeletonResponse.status,
            response_time: skeletonTime
          });
          console.error("Skeleton generation failed", skeletonResponse);
          return;
        }
  
        const skeletonData = await skeletonResponse.json();
        const skeletonTime = performance.now() - skeletonStartTime;
        
        Mixpanel.performance('Skeleton Generation', {
          responseTime: skeletonTime,
          loadTime: skeletonTime
        }, {
          startup_slug: data.startup_entity.slug,
          roadmap_id: skeletonData.roadmap_id,
          success: true
        });
        
        console.log("Skeleton data:", skeletonData);
  
        get().appendCategoryToProgram(skeletonData);
  
        const taskId = skeletonData.roadmap_id;
  
        // 3. Polling Phase
        const pollingStartTime = performance.now();
        let pollCount = 0;
        let isCompleted = false;
        
        while (!isCompleted) {
          pollCount++;
          const statusResponse = await fetch(`${config.status}/${taskId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "User-Agent": "RoadmapClient/1.0",
            },
          });
  
          if (!statusResponse.ok) {
            const pollingTime = performance.now() - pollingStartTime;
            Mixpanel.error('status_polling', {
              message: 'Failed to fetch task status',
              startup_slug: data.startup_entity.slug,
              task_id: taskId,
              poll_count: pollCount,
              total_polling_time: pollingTime,
              status_code: statusResponse.status
            });
            console.error("Failed to fetch task status", statusResponse);
            break;
          }
  
          const statusData = await statusResponse.json();
          console.log("Task status:", statusData);
  
          if (statusData.status === "completed") {
            const pollingTime = performance.now() - pollingStartTime;
            
            // Track polling completion
            Mixpanel.performance('Status Polling', {
              responseTime: pollingTime,
              loadTime: pollingTime
            }, {
              startup_slug: data.startup_entity.slug,
              task_id: taskId,
              total_polls: pollCount,
              final_status: statusData.status,
              success: true
            });
            
            console.log("Task reported completed. Verifying data...");
  
            // 4. Enhanced Verification with 4 attempts
            const verificationStartTime = performance.now();
            let verificationAttempts = 0;
            let programAvailable = false;
            const maxVerificationAttempts = 4;
  
            for (let attempt = 1; attempt <= maxVerificationAttempts; attempt++) {
              verificationAttempts++;
              await new Promise((resolve) => setTimeout(resolve, 2000));
  
              try {
                const startupResponse = await fetchApi(
                  `/startup_entities/${get().slug}`,
                  "GET"
                );
  
                if (startupResponse.ok) {
                  const latestStartupData = await startupResponse.json();
                  console.log(`Verification attempt ${attempt}:`, latestStartupData);
  
                  if (latestStartupData.startup_entity?.program) {
                    console.log("Verification successful! Program data is available.");
                    
                    const verificationTime = performance.now() - verificationStartTime;
                    Mixpanel.custom('Program Verification Success', {
                      startup_slug: data.startup_entity.slug,
                      verification_attempts: verificationAttempts,
                      verification_time: verificationTime,
                      successful_attempt: attempt
                    });
                    
                    get().setStartup(latestStartupData);
                    // After verification success, we avoid triggering UI waits here
                    // to prevent flicker; the wait and notifications occur after
                    // the tasks generation step below.
                    programAvailable = true;
                    break;
                  } else {
                    console.log(`Verification attempt ${attempt} failed. Program data not yet available.`);
                  }
                } else {
                  Mixpanel.error('verification_fetch', {
                    message: 'Failed to refresh startup data during verification',
                    startup_slug: data.startup_entity.slug,
                    verification_attempt: attempt,
                    status_code: startupResponse.status
                  });
                  console.error(`Verification attempt ${attempt} - API call failed:`, startupResponse);
                }
              } catch (refreshError) {
                Mixpanel.error('verification_exception', {
                  message: 'Error during startup data refresh verification',
                  startup_slug: data.startup_entity.slug,
                  verification_attempt: attempt,
                  error_message: refreshError.message,
                  error_stack: refreshError.stack
                });
                console.error(`Verification attempt ${attempt} - Exception:`, refreshError);
              }
            }
  
            // If all verification attempts failed
            if (!programAvailable) {
              const verificationTime = performance.now() - verificationStartTime;
              const totalProcessTime = performance.now() - processStartTime;
              
              Mixpanel.custom('Program Verification Failed - Page Reload', {
                startup_slug: data.startup_entity.slug,
                verification_attempts: verificationAttempts,
                verification_time: verificationTime,
                total_process_time: totalProcessTime,
                task_id: taskId,
                final_status: 'completed_but_program_unavailable'
              });
              
              console.log("All verification attempts failed. Reloading page...");
              await new Promise((resolve) => setTimeout(resolve, 2000)); // Final delay
              window.location.reload();
              return;
            }
  
            // 5. Generate Tasks
            const tasksStartTime = performance.now();
            const tasksResponse = await fetchApi(
              `/startup_entities/${get().slug}/fetch_subcategory_tasks`,
              "GET"
            );
            
            const tasksTime = performance.now() - tasksStartTime;
            
            if (tasksResponse.ok) {
              Mixpanel.performance('Task Generation', {
                responseTime: tasksTime,
                loadTime: tasksTime
              }, {
                startup_slug: data.startup_entity.slug,
                success: true
              });
              console.log("Tasks generation initiated successfully");
              // After tasks job is queued, wait for availability before notifying UI
              Promise.resolve().then(async () => {
                try {
                  const state = get();
                  const cid = state.selectedProgram?.cid;
                  const sid = state.selectedProgram?.sid;
                  const subcategoryId = state.content?.[cid]?.[sid]?.id;

                  async function waitForSuggestedTasks(subId, { attempts = 12, intervalMs = 1000 } = {}) {
                    if (!subId) return false;
                    for (let i = 0; i < attempts; i++) {
                      try {
                        const res = await fetchApi(`/sub_categories/${subId}/suggested_tasks`, "GET");
                        if (res.ok) {
                          const data = await res.json().catch(() => ({}));
                          const hasAny = Array.isArray(data?.suggested_tasks) && data.suggested_tasks.length > 0;
                          if (hasAny) return true;
                        }
                      } catch (_) {}
                      await new Promise((r) => setTimeout(r, intervalMs));
                    }
                    return false;
                  }
                } catch (_) {}
                finally {
                  // Clear dedicated loading
                  set({ suggestedTasksLoading: false });
                }
              });
            } else {
              Mixpanel.error('task_generation', {
                message: 'Tasks generation failed',
                startup_slug: data.startup_entity.slug,
                status_code: tasksResponse.status,
                response_time: tasksTime
              });
              console.error("Tasks generation failed", tasksResponse);
            }
  
            // 6. Generate Folders
            const foldersStartTime = performance.now();
            const foldersResponse = await fetchApi(
              `/startup_entities/${get().slug}/generate_folders`,
              "POST"
            );
            
            const foldersTime = performance.now() - foldersStartTime;
            
            if (foldersResponse.ok) {
              Mixpanel.performance('Folder Generation', {
                responseTime: foldersTime,
                loadTime: foldersTime
              }, {
                startup_slug: data.startup_entity.slug,
                success: true
              });
              console.log("Folder generation job queued successfully");
            } else {
              Mixpanel.error('folder_generation', {
                message: 'Folder generation job queuing failed',
                startup_slug: data.startup_entity.slug,
                status_code: foldersResponse.status,
                response_time: foldersTime
              });
              console.error("Folder generation job queuing failed", foldersResponse);
            }
  
            // 7. Track Overall Success
            const totalProcessTime = performance.now() - processStartTime;
            Mixpanel.custom('Roadmap Generation Completed', {
              startup_slug: data.startup_entity.slug,
              total_process_time: totalProcessTime,
              summary_fetch_time: summaryTime,
              skeleton_generation_time: skeletonTime,
              polling_time: pollingTime,
              verification_attempts: verificationAttempts,
              total_polls: pollCount,
              tasks_generated: tasksResponse.ok,
              folders_generated: foldersResponse.ok,
              success: true
            });
  
            isCompleted = true;
            break;
          }
  
          if (!isCompleted) {
            await new Promise((resolve) => setTimeout(resolve, 3000));
          }
        }
  
      } catch (error) {
        const totalProcessTime = performance.now() - processStartTime;
        Mixpanel.error('roadmap_generation_exception', {
          message: 'Error generating roadmap',
          startup_slug: data.startup_entity.slug,
          error_message: error.message,
          error_stack: error.stack,
          total_process_time: totalProcessTime,
          severity: 'critical'
        });
        console.error("Error generating roadmap:", error);
      }
    }
  },
  

  setChats: (newChats) => {
    set((state) => {
      const categoryIndex = state.selectedProgram.cid;
      const subIndex = state.selectedProgram.sid;

      // Update the chats in the content state
      const updatedSubcategoryContent = {
        ...(state.content[categoryIndex]?.[subIndex] || {}),
        chats: newChats,
      };

      return {
        content: {
          ...state.content,
          [categoryIndex]: {
            ...(state.content[categoryIndex] || {}),
            [subIndex]: updatedSubcategoryContent,
          },
        },
      };
    });
  },

  setStartupImage: (image) =>
    set((state) => ({
      startup: { ...state.startup, image },
    })),

  setSelectedProgram: (s) =>
    set((state) => ({
      selectedProgram: { ...state.selectedProgram, ...s },
    })),

    setProgram: (s) => {
      // Use set to update the program state
      set((state) => ({
        program: { ...state.program, ...s },
      }));
      // Use get() to access and call the setEditorState action defined in the store
      get().setEditorState();
    },
    
   updateSubCategoryStatusInProgram: (subCategoryId, newStatus) => {
      const { program } = get();
      if (!program || !program.categories) return;
      
      const updatedCategories = program.categories.map((category) => {
        // Support both property names for subcategories
        if (category.subcategories && Array.isArray(category.subcategories)) {
          const updatedSubcategories = category.subcategories.map((sub) =>
            sub.id === subCategoryId ? { ...sub, status: newStatus } : sub
          );
          return { ...category, subcategories: updatedSubcategories };
        } else if (category.sub_categories && Array.isArray(category.sub_categories)) {
          const updatedSubcategories = category.sub_categories.map((sub) =>
            sub.id === subCategoryId ? { ...sub, status: newStatus } : sub
          );
          return { ...category, sub_categories: updatedSubcategories };
        }
        return category;
      });
  
      // Update the program in the store with the modified categories
      set({
        program: {
          ...program,
          categories: updatedCategories,
        },
      });
    },

    setCategory: (cat) => {
      set((state) => {
        const exists = state.program?.categories?.find((c) => c.id === cat.id);
        const updatedCategories = exists
          ? state.program.categories.map((c) => (c.id === cat.id ? cat : c))
          : [...(state.program.categories || []), cat];
    
        const updatedProgram = {
          ...state.program,
          categories: updatedCategories,
        };
    
        const updatedContentForCategory = {};
        const updatedPrivacyForCategory = {};
        const updatedFilesForCategory = {};
    
        (cat.sub_categories || []).forEach((subCategory, subIndex) => {
          updatedContentForCategory[subIndex] = {
            response: subCategory.proposed_response || subCategory.response,
            originalResponse: subCategory.response,
            diff: subCategory.diff || null,
            touched: false,
            id: subCategory.id,
            category_id: cat.id,
            name: subCategory.name,
            guide: subCategory.guide,
            wait: subCategory.waiting_for_approval,
            chats: subCategory.limited_conversation_histories,
          };
    
          updatedPrivacyForCategory[subIndex] = subCategory.privacy;
          updatedFilesForCategory[subIndex] = [
            ...(Array.isArray(subCategory.file_records)
              ? subCategory.file_records
              : []),
            ...(Array.isArray(subCategory.files_attachments)
              ? subCategory.files_attachments
              : []),
          ];
        });
    
        // If the category is new, assign its index as the last element.
        const categoryIndex = exists
          ? state.selectedProgram.cid
          : updatedProgram.categories.length - 1;
    
        return {
          program: updatedProgram,
          content: {
            ...state.content,
            [categoryIndex]: updatedContentForCategory,
          },
          privacy: {
            ...state.privacy,
            [categoryIndex]: updatedPrivacyForCategory,
          },
          files: {
            ...state.files,
            [categoryIndex]: updatedFilesForCategory,
          },
        };
      });
    },
    

  setSubcategory: (sub) => {
    set((state) => ({
      program: {
        ...state.program,
        categories: state.program?.categories?.map((c, i) =>
          i == state.selectedProgram.cid
            ? {
                ...c,
                sub_categories: c.sub_categories.map((s, i) =>
                  i == state.selectedProgram.sid ? sub : s
                ),
              }
            : c
        ),
      },
    }));
  },

  setOpenings: (s) =>
    set((state) => ({
      openings: [...state.openings, s],
    })),

  setMembers: (s) =>
    set((state) => ({
      members: [...state.members, s],
    })),

  removeMembers: (id) =>
    set((state) => ({
      members: state.members.filter((p) => p.id != id),
      isMember: false,
    })),

  removeOpenings: (id) =>
    set((state) => ({
      openings: state.openings.filter((p) => p.id != id),
    })),

  setIsSubmitted: (submitted) =>
    set((state) => ({
      isSubmitted: submitted,
    })),

  setSubmittedAt: (submitTime) =>
    set((state) => ({
      submittedAt: submitTime,
    })),

  toggleOpenChat: () => set((state) => ({ openChat: !state.openChat })),
  toggleShowFunding: () =>
    set((state) => ({ showFunding: !state.showFunding })),

  logTime: async (slug) => {
    const { startTime } = get();
    const endTime = Date.now();
    const timeSpentInMinutes = (endTime - startTime) / 1000 / 60;

    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/log_time`,
        "POST",
        {
          time_spent: timeSpentInMinutes,
        }
      );

      if (response.ok) {
        const res = await response.json();
        console.log("Time logged successfully:", res);
      } else {
        const msg = (await response.json()).error;
        console.error("Failed to log time:", msg);
      }
    } catch (error) {
      console.error("Error logging time:", error);
    }
  },

  handleFileChange: async (event, slug) => {
    const selectedFile = event.target.files[0];
    const imageURL = URL.createObjectURL(selectedFile);
    set({ startupImage: imageURL });

    try {
      const formData = new FormData();
      formData.append("startup_entity[image]", selectedFile);
      const response = await fetchApi(
        `/startup_entities/${slug}`,
        "PATCH",
        formData,
        true
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log("File updated successfully:", responseData);
      } else {
        console.error("Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading file:", error.message);
    }
  },

  submitPendingChanges: async () => {
    const { pendingChanges, content, privacy, files, setStartup, slug, selectedProgram } = get();

    // Commit only the CURRENT subcategory (cid/sid) and only if it has pending changes
    const categoryIndex = selectedProgram.cid;
    const subIndex = selectedProgram.sid;

    const categoryPending = pendingChanges[categoryIndex] || {};
    const changeFlags = categoryPending[subIndex];

    // Create a FormData instance for a multipart/form-data request
    const differ = new EditorJsDiff();
    const formData = new FormData();
    const changes = [];
    

    // Build payload only for the current subcategory
    const subcategoryContent = content[categoryIndex]?.[subIndex];
    const subcategoryPrivacy = privacy[categoryIndex]?.[subIndex];
    const subcategoryFilesAll = files[categoryIndex]?.[subIndex] || [];
    const subcategoryFiles = subcategoryFilesAll.filter((file) => {
      if (file.source === "local") {
        return true;
      } else {
        return file.isNew === true;
      }
    });

    if (!subcategoryContent) {
      console.error(
        `Missing content for category ${categoryIndex}, subcategory ${subIndex}`
      );
      return { success: false, message: "Missing content for this section." };
    }

    const updatePayload = {
      categoryId: subcategoryContent.category_id,
      subcategoryId: subcategoryContent.id,
      updates: {},
    };

    // Allow commit if either pending flag exists or editor content differs from last committed
    const hasContentDiff = subcategoryContent.response !== subcategoryContent.originalResponse;
    if (changeFlags?.content || hasContentDiff) {
      updatePayload.updates.response = subcategoryContent.response;
      updatePayload.updates.diff = differ.compareContent(
        subcategoryContent.originalResponse,
        subcategoryContent.response
      );
    }

    if (changeFlags?.privacy) {
      updatePayload.updates.privacy = subcategoryPrivacy;
    }

    if (
      changeFlags?.files &&
      Array.isArray(subcategoryFiles) &&
      subcategoryFiles.length > 0
    ) {
      const localFiles = subcategoryFiles.filter((file) => file.source === "local");
      const cloudFiles = subcategoryFiles.filter((file) => file.source !== "local" && file.url);

      if (localFiles.length > 0) {
        localFiles.forEach((file) => {
          formData.append(
            `local_files[${subcategoryContent.id}][]`,
            file.file,
            file.file.name
          );
        });
        updatePayload.updates.has_local_files = true;
      }

      if (cloudFiles.length > 0) {
        updatePayload.updates.file_records_attributes = cloudFiles.map((file) => ({
          name: file.name || "Unknown",
          url: file.url,
          file_id: file.id,
          size_bytes: file.size_bytes,
          icon: file.icon || file.thumbnail,
          source: file.source,
        }));
      }
    }

    if (Object.keys(updatePayload.updates).length > 0) {
      changes.push(updatePayload);
    }

    // Append the changes JSON and any other necessary fields
    formData.append("changes", JSON.stringify(changes));
    formData.append("slug", slug);

    try {
      // Ensure your fetchApi (or equivalent) sends the FormData as multipart/form-data.
      const response = await fetchApi(
        "/sub_categories/bulk_update",
        "PATCH",
        formData,
        true
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || response.statusText);
      }

      // Refresh startup data so committed content and versions reflect
      const startupResponse = await fetchApi(
        `/startup_entities/${slug}`,
        "GET"
      );
      if (startupResponse.ok) {
        const startupData = await startupResponse.json();
        setStartup(startupData);
        // Clear pending flags only for the committed subcategory
        set((state) => {
          const newPending = { ...(state.pendingChanges || {}) };
          if (newPending[categoryIndex]) {
            const { [subIndex]: _removed, ...restSubs } = newPending[categoryIndex];
            if (Object.keys(restSubs).length > 0) {
              newPending[categoryIndex] = restSubs;
            } else {
              const { [categoryIndex]: _catRemoved, ...restCats } = newPending;
              return { pendingChanges: restCats };
            }
          }
          return { pendingChanges: newPending };
        });
      }

      return {
        success: true,
        message: "Changes submitted successfully",
        changesCount: changes.length,
      };
    } catch (error) {
      console.error("Error submitting changes:", error);
      return {
        success: false,
        error: error.message,
        changesCount: changes.length,
      };
    }
  },

  appendCategoryToProgram: (skeleton) => {
    // Map the skeleton steps to categories
    const mappedCategories = skeleton.steps.map((step) => ({
      name: step.step_name,
      id: step.step_name,
      start_date: step.subcategories[0]?.start_date || null, // Use the first subcategory's start date
      sub_categories: step.subcategories.map((sub, index) => ({
        name: sub.name,
        id: index,
        privacy: "Everyone",
        response: {
          blocks: [{ type: "paragraph", data: { text: sub.outcome } }],
        },
        is_active: true,
        is_editable: true,
      })),
    }));

    // Update the program state with the new categories
    set((state) => {
      const existingCategories = state.program?.categories || [];
      return {
        program: {
          ...state.program,
          categories: [...existingCategories, ...mappedCategories],
        },
      };
    });
  },
}));

export default useStartupStore;
